import { IView } from '../../registry/iface/iView.js';
import { JsonObject } from '../../app/json/jsonValue.js';
import { Logger } from '@arshu/common/logger';

// Param key naming the handler's wwwroot-relative script path (forward-slash, no leading slash).
const JS_RELATIVE_KEY = 'JsRelative';

/**
 * Auto-injects an AppSite-local handler script reference into a component (PLAN_HANDLER_FIX §18). The handler
 * travels WITH its AppSite as a droppable unit under Data/, and its wwwroot-relative location is declared by
 * the component's own DynamicView param (JsRelative) — a data file that ships inside the AppSite folder, so a
 * shipped binary is extended with a new dynamic AppSite (handler included) without a recompile. Phase 1: the
 * reference is inert text (never executed by the console/crosslang merge), so the render stays deterministic.
 *
 * Like BaseView/RuleView this runs on BOTH the server render and the client re-merge, and stays axis-identical
 * because it reads the path from the SAME param the loader ships on every axis (the param travels in the
 * api.json DynamicView store ConfigClient/ConfigOCI already carry) — so the injected bytes match everywhere.
 */
export class HandlerView extends IView {
    /**
     * Gets the full type name for DynamicView operation tracking
     */
    get typeName(): string {
        return 'HandlerView';
    }

    /**
     * Appends the local-handler <script> reference to the component HTML.
     */
    getView(
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        resolvedAppSite: string,
        resolvedComponentName: string,
        componentHtml: string,
        paramJson: JsonObject,
        componentJson: JsonObject,
        configJson: JsonObject | null = null
    ): string {
        try {
            const src = HandlerView.resolveHandlerSrc(paramJson, componentJson);
            if (!src) {
                return componentHtml;
            }
            return componentHtml + HandlerView.buildScriptTag(src);
        } catch (ex) {
            Logger.error((ex as Error).message, 'HandlerView');
            return componentHtml;
        }
    }

    /**
     * Appends the local-handler script reference to the HtmlJson structure as a trailing static entry,
     * preserving the component's existing sequenced entries.
     */
    getViewJson(
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        resolvedAppSite: string,
        resolvedComponentName: string,
        componentHtmlJson: JsonObject,
        paramJson: JsonObject,
        componentJson: JsonObject,
        configJson: JsonObject | null = null
    ): JsonObject {
        try {
            const src = HandlerView.resolveHandlerSrc(paramJson, componentJson);
            if (!src) {
                return componentHtmlJson;
            }

            const result = new JsonObject();
            let sequence = 1;

            // Preserve _meta for JSON merge to work correctly
            if (componentHtmlJson.has('_meta')) {
                result.set('_meta', componentHtmlJson.get('_meta'));
            }

            // Copy existing entries with renumbered sequence keys (preserve type prefix and name)
            for (const key of componentHtmlJson.keys()) {
                if (key === '_meta') continue;

                const keyType = key.length > 0 ? key[0] : '\0';
                let keyName = '';
                const underscorePos = key.indexOf('_');
                if (underscorePos >= 0) {
                    keyName = key.substring(underscorePos + 1);
                }

                const newKey = !keyName
                    ? `${keyType}${sequence}`
                    : `${keyType}${sequence}_${keyName}`;
                result.set(newKey, componentHtmlJson.get(key));
                sequence++;
            }

            // Append the handler script reference as a trailing static (s) entry
            result.set(`s${sequence}`, HandlerView.buildScriptTag(src));

            return result;
        } catch (ex) {
            Logger.error((ex as Error).message, 'HandlerView.GetViewJson');
            return componentHtmlJson;
        }
    }

    /**
     * Reads the declared handler script path from paramJson first, then componentJson. Returns empty when no
     * path is declared, so the component stays byte-identical.
     */
    private static resolveHandlerSrc(paramJson: JsonObject | null, componentJson: JsonObject | null): string {
        let src = HandlerView.readJsRelative(paramJson);
        if (!src) {
            src = HandlerView.readJsRelative(componentJson);
        }
        return src;
    }

    private static readJsRelative(json: JsonObject | null): string {
        if (json && json.containsKey(JS_RELATIVE_KEY)) {
            const path = json.getString(JS_RELATIVE_KEY);
            if (path) {
                return path;
            }
        }
        return '';
    }

    /**
     * Builds the module-script tag for a wwwroot-relative handler path. Deterministic (no timestamp) so the
     * injected bytes are identical across every render and every language port.
     */
    private static buildScriptTag(src: string): string {
        return `<script type="module" src="/${src}"></script>`;
    }
}
